import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Button, Chip, IconButton } from '@c2fo/liquidity';
import {
  Circle1LightIcon,
  Circle2LightIcon,
  Circle3LightIcon,
  UploadLightIcon,
  DownloadIcon,
  FileLightIcon,
} from '@c2fo/liquidity/icons';
import { useCurrencyContext } from '@/contexts/CurrencyContext';
import BuyerLogo from '@/enterprise/components/BuyerLogo';
import Skeleton from '@/enterprise/components/Skeleton';
// The signed document is a Supplier Integration document like any other — this hook hits the same
// generic-by-uuid metadata route the company-document cards use, despite its "company" naming.
import useCompanyDocumentMetadata from '@/enterprise/data/useCompanyDocumentMetadata';
import { BuyerGroupWithStats } from '@/enterprise/data/useGroupByBuyer';
import { RemittanceEnvelope } from '@/enterprise/data/useRemittanceEnvelopes';
import useUploadSignedRemittanceDocument from '@/enterprise/data/useUploadSignedRemittanceDocument';
import apiClient from '@/lib/apiClient';
import { useReporting } from '@/reporting';
import { parseContentDispositionFilename, triggerBlobDownload } from '@/utils/downloadCsvFromPost';
import formatFileSize from '@/utils/formatFileSize';
import handleNaN from '@/utils/handleNaN';
import useLocaleFormat from '@/utils/useLocaleFormat';
import { trackInstantPayOnboarding } from './trackInstantPayOnboarding';

const MAX_FILE_SIZE_BYTES = 10 * 1024 * 1024;

type FileValidationError = 'type' | 'size' | null;

interface CustomerRoutingCardProps {
  buyer: BuyerGroupWithStats;
  envelope: RemittanceEnvelope | undefined;
  isFundingConfirmed: boolean;
}

const CustomerRoutingCard = ({ buyer, envelope, isFundingConfirmed }: CustomerRoutingCardProps) => {
  const { t } = useTranslation();
  const { asCurrency, asNumber } = useLocaleFormat();
  const { trackEnterpriseEvent } = useReporting();
  const { selectedCurrency } = useCurrencyContext();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [fileError, setFileError] = useState<FileValidationError>(null);
  const [isDownloadingSource, setIsDownloadingSource] = useState(false);
  const [sourceDownloadError, setSourceDownloadError] = useState(false);
  const [isDownloadingSigned, setIsDownloadingSigned] = useState(false);
  const [signedDownloadError, setSignedDownloadError] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const uploadMutation = useUploadSignedRemittanceDocument();

  const signedDocumentUuid = envelope?.signedDocumentUuid ?? null;
  const isSigned = signedDocumentUuid !== null;
  // The file the user picked in the current session already has a real name/size client-side —
  // metadata is only fetched for a document uploaded in a prior session (see CompanyDocumentUpload).
  const metadata = useCompanyDocumentMetadata(selectedFile ? null : signedDocumentUuid);
  // The metadata query is only enabled once signedDocumentUuid is set and no file is selected
  // client-side (see above) — `metadata.isPending` alone would stay true forever while disabled.
  const isLoadingMetadata = !selectedFile && signedDocumentUuid !== null && metadata.isPending;

  const documentFileName = selectedFile ? selectedFile.name : metadata.data?.fileName;
  const documentFileSize = selectedFile
    ? formatFileSize(selectedFile.size)
    : metadata.data?.size != null
    ? formatFileSize(metadata.data.size)
    : undefined;

  const handleDownloadSource = async () => {
    if (!envelope) {
      return;
    }

    setIsDownloadingSource(true);
    setSourceDownloadError(false);

    try {
      const response = await apiClient.get(
        `api/supplier-experience/remittance-envelopes/${envelope.envelopeUuid}/source-document`
      );
      const filename =
        parseContentDispositionFilename(response.headers.get('content-disposition')) ?? 'remittance-form.pdf';
      triggerBlobDownload(await response.blob(), filename);
      trackInstantPayOnboarding(trackEnterpriseEvent, {
        step: 3,
        action: 'downloaded',
        makerOrganizationUuid: buyer.makerOrganizationUuid,
        envelopeUuid: envelope.envelopeUuid,
      });
    } catch {
      setSourceDownloadError(true);
    } finally {
      setIsDownloadingSource(false);
    }
  };

  const handleDownloadSigned = async () => {
    // The file just picked this session is already in memory — no need to re-fetch it.
    if (selectedFile) {
      triggerBlobDownload(selectedFile, selectedFile.name);
      return;
    }

    if (!signedDocumentUuid) {
      return;
    }

    setIsDownloadingSigned(true);
    setSignedDownloadError(false);

    try {
      const blob = await apiClient
        .get(`api/supplier-experience/companies/documents/${signedDocumentUuid}/content`)
        .blob();
      triggerBlobDownload(blob, documentFileName ?? t('instantPay.onboarding.customerCard.uploadCta'));
    } catch {
      setSignedDownloadError(true);
    } finally {
      setIsDownloadingSigned(false);
    }
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] ?? null;
    event.target.value = '';

    if (!file || !envelope) {
      return;
    }

    if (file.type !== 'application/pdf') {
      setFileError('type');
      return;
    }

    if (file.size > MAX_FILE_SIZE_BYTES) {
      setFileError('size');
      return;
    }

    setFileError(null);
    setSelectedFile(file);
    uploadMutation.mutate(
      { envelopeUuid: envelope.envelopeUuid, file },
      {
        onSuccess: () =>
          trackInstantPayOnboarding(trackEnterpriseEvent, {
            step: 3,
            action: 'uploaded',
            makerOrganizationUuid: buyer.makerOrganizationUuid,
            envelopeUuid: envelope.envelopeUuid,
          }),
      }
    );
  };

  return (
    <div
      data-testid={`customer-routing-card-${buyer.makerOrganizationUuid}`}
      className="flex w-full flex-col gap-4 rounded-xl bg-canvas p-4 shadow-sm"
    >
      <div className="flex min-w-0 items-center gap-4">
        <BuyerLogo
          buyerLogoUrl={buyer.makerOrganizationLogo}
          buyerName={buyer.makerOrganizationName}
          size="2xl"
          className="shadow-sm"
        />
        <div className="flex min-w-0 flex-col gap-2">
          <div className="flex flex-wrap items-center gap-2 md:flex-nowrap">
            <span className="truncate text-2xl font-bold text-text-primary">{buyer.makerOrganizationName}</span>
            {isFundingConfirmed ? (
              <Chip
                label={t('instantPay.onboarding.customerCard.complete')}
                variant="outlined"
                isRound
                size="xs"
                type="primary"
              />
            ) : (
              isSigned && (
                <Chip
                  label={t('instantPay.onboarding.customerCard.inReview')}
                  variant="outlined"
                  isRound
                  size="xs"
                  type="warning"
                />
              )
            )}
          </div>
          <div className="flex flex-col text-base text-text-primary md:flex-row md:items-center md:gap-1">
            <span className="truncate">
              {t('instantPay.onboarding.customerCard.eligibleAmount', {
                amount: asCurrency(buyer.stats.eligibleInvoiceAmount, selectedCurrency),
              })}
            </span>
            <span className="hidden md:inline">|</span>
            <span className="truncate">
              {t('instantPay.onboarding.customerCard.avgDpe', {
                dpe: asNumber({ value: handleNaN(buyer.stats.avgDaysPaidEarly), decimals: 0 }),
              })}
            </span>
          </div>
        </div>
      </div>

      <div className="flex flex-col gap-2 rounded-xl bg-white p-4">
        <p className="text-lg font-medium text-text-primary">{t('instantPay.onboarding.customerCard.howToUpdate')}</p>

        <div className="flex flex-col gap-4">
          <div className="flex flex-col items-start gap-4 md:flex-row md:items-center md:justify-between">
            <div className="flex items-center gap-2">
              <Circle1LightIcon className="size-5 shrink-0 fill-text-primary" />
              <span className="text-base text-text-primary">
                {t('instantPay.onboarding.customerCard.downloadStep')}
              </span>
            </div>
            {!isSigned && (
              <Button
                variant="ancillary"
                size="md"
                disabled={!envelope?.sourceDocumentUuid}
                loading={isDownloadingSource}
                icon={<DownloadIcon className="fill-secondary-500" />}
                onClick={handleDownloadSource}
                className="[&>span>svg]:size-[18px]"
              >
                {t('instantPay.onboarding.customerCard.downloadCta')}
              </Button>
            )}
          </div>
          {sourceDownloadError && (
            <Alert
              full
              type="error"
              variant="outlined"
              description={t('instantPay.onboarding.customerCard.downloadError')}
            />
          )}

          <div className="flex flex-col gap-4 md:flex-row md:items-center md:gap-6">
            <div className="flex flex-1 items-center gap-2">
              <Circle2LightIcon className="size-5 shrink-0 fill-text-primary" />
              <div className="flex flex-col">
                <span className="text-base text-text-primary">
                  {t('instantPay.onboarding.customerCard.uploadStep')}
                </span>
                <span className="text-sm text-text-secondary">
                  {t('instantPay.onboarding.customerCard.uploadStepDescription')}
                </span>
              </div>
            </div>
            <div className="flex flex-1 items-center md:justify-end">
              {isSigned ? (
                isLoadingMetadata ? (
                  <div className="flex w-full items-center gap-4 rounded-lg bg-white p-4 shadow">
                    <Skeleton height="h-10" shadow={false} className="w-10 shrink-0 rounded-lg" />
                    <div className="flex flex-1 flex-col gap-2">
                      <Skeleton height="h-4" shadow={false} className="w-1/3 rounded" />
                      <Skeleton height="h-4" shadow={false} className="w-1/5 rounded" />
                    </div>
                    <Skeleton height="h-9" shadow={false} className="w-9 shrink-0 rounded-full" />
                  </div>
                ) : (
                  <div className="flex w-full items-center justify-between gap-4 rounded-lg bg-white p-4 shadow">
                    <div className="flex min-w-0 flex-1 items-center gap-2">
                      <div className="flex shrink-0 items-center justify-center rounded-lg bg-deepGray-50 p-2">
                        <FileLightIcon className="size-6 fill-text-secondary" />
                      </div>
                      <div className="flex min-w-0 flex-1 flex-col">
                        <span className="truncate text-text-primary">
                          {documentFileName ?? t('instantPay.onboarding.documents.metadataError')}
                        </span>
                        <div className="flex flex-col items-start gap-1 md:flex-row md:items-center md:gap-2">
                          {documentFileSize && <span className="text-text-secondary">{documentFileSize}</span>}
                          <Chip
                            label={t('instantPay.onboarding.documents.uploaded')}
                            variant="outlined"
                            isRound
                            size="xs"
                            type="primary"
                          />
                        </div>
                      </div>
                    </div>
                    <IconButton
                      icon={DownloadIcon}
                      name={t('instantPay.onboarding.documents.downloadUploaded')}
                      variant="ancillary"
                      size="sm"
                      loading={isDownloadingSigned}
                      onClick={handleDownloadSigned}
                      className="[&>svg]:size-5"
                    />
                  </div>
                )
              ) : (
                <>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="application/pdf"
                    className="hidden"
                    onChange={handleFileChange}
                  />
                  <Button
                    variant="primary"
                    size="md"
                    disabled={!envelope?.sourceDocumentUuid}
                    loading={uploadMutation.isPending}
                    icon={<UploadLightIcon className="fill-white" />}
                    onClick={() => fileInputRef.current?.click()}
                  >
                    {t('instantPay.onboarding.customerCard.uploadCta')}
                  </Button>
                </>
              )}
            </div>
          </div>
          {fileError && (
            <Alert
              full
              type="error"
              variant="outlined"
              description={t(
                fileError === 'type'
                  ? 'instantPay.onboarding.customerCard.fileTypeError'
                  : 'instantPay.onboarding.customerCard.fileSizeError'
              )}
            />
          )}
          {uploadMutation.isError && (
            <Alert
              full
              type="error"
              variant="outlined"
              description={t('instantPay.onboarding.customerCard.uploadError')}
            />
          )}
          {signedDownloadError && (
            <Alert
              full
              type="error"
              variant="outlined"
              description={t('instantPay.onboarding.documents.downloadError')}
            />
          )}

          <div className="flex items-center gap-2">
            <Circle3LightIcon className="size-5 shrink-0 fill-text-primary" />
            <div className="flex flex-col">
              <span className="text-base text-text-primary">
                {t('instantPay.onboarding.customerCard.awaitValidation')}
              </span>
              <span className="text-sm text-text-secondary">
                {t('instantPay.onboarding.customerCard.awaitValidationDescription')}
              </span>
            </div>
          </div>

          {isSigned && !isFundingConfirmed && (
            <p className="w-full text-center text-sm italic text-warning-900">
              <span className="font-medium">{t('instantPay.onboarding.customerCard.validationInProgressTitle')}</span>
              {' - '}
              <span className="font-normal">
                {t('instantPay.onboarding.customerCard.validationInProgressDescription')}
              </span>
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default CustomerRoutingCard;
